import React, { useContext, useMemo } from 'react'

import { Link } from 'react-router-dom'

import {
  Box,
  Health,
  List,
  ListItem,
  makeAppStyles,
  ProviderLogo,
} from '@percept/mui'

import { OpenInNew } from '@percept/mui/icons'

import { CampaignSetupView } from './CampaignSetupView'

import { cardLoadingStyles, TimeseriesContext, OrgUnitContext } from '@percept/app-components'

import {
  OrgUnitProviderCategoryScoreTimeseriesDatum,
  TimeseriesHealthDatum,
  useChildOrgUnitCategoryScores,
  useOrgUnitCategoryScores,
} from '@percept/hooks'

import { BubbleChartDatum } from './BubbleChart'

import { flatten, get, intersection, sortBy } from 'lodash-es'

import { CampaignSetupContext } from './context'

import { CAMPAIGN_SETUP_GRANULARITY, getPreviousTimeseriesDateRange, getTimeseriesDateRange, useReportAvailability } from './lib'

import { providerLabelMap, providerURLMap, reportProvidersV2 } from '@percept/constants'

import { HealthType, ReportProviderV2 } from '@percept/types'


type OrgUnitDatum = {
  id: string
  name: string
  health: number | null
}

const responseAdapter = (
  response: OrgUnitProviderCategoryScoreTimeseriesDatum[]
): BubbleChartDatum<OrgUnitDatum, ReportProviderV2>[] => {
  const overallDataByProvider: Partial<Record<ReportProviderV2, TimeseriesHealthDatum[]>> = {}

  for( const d of response ){
    overallDataByProvider[d.provider] = [
      ...overallDataByProvider[d.provider] || [],
      ...d.timeseriesByCategory.OVERALL,
    ]
  }

  const allData = flatten(
    Object.values(overallDataByProvider)
  )

  const latestTimestamp = Math.max(...allData.map( d => d.start.getTime()))

  return response.reduce( (acc, item) => {
    const { provider, org_unit_name, org_unit_id } = item
    item.timeseriesByCategory.OVERALL.forEach( d => {
      if( d.start.getTime() === latestTimestamp && d.health !== null ){
        acc.push({
          key: provider,
          column: provider as ReportProviderV2,
          health: d.health,
          data: {
            id: org_unit_id,
            name: org_unit_name,
            health: d.health,
          },
        })
      }
    })
    return acc
  }, [] as BubbleChartDatum<OrgUnitDatum, ReportProviderV2>[])
}

const useStyles = makeAppStyles( theme => ({
  ...cardLoadingStyles,
  providerLink: {
    display: 'flex',
    alignItems: 'center',
    transition: theme.transitions.create('color'),
    fontSize: 13,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  linkIcon: {
    marginRight: theme.spacing(0.75),
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  health: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
  },
}))


export const MultiOrgCampaignSetup = () => {
  const orgUnit = useContext(OrgUnitContext)

  const availability = useReportAvailability()

  const [urlState] = useContext(CampaignSetupContext)
  
  const minDate = availability && availability.start
  const maxDate = availability && availability.end

  const referenceDate = (
    urlState.referenceDate === 'latest' ?
      maxDate :
      new Date(urlState.referenceDate)
  )

  const granularity = CAMPAIGN_SETUP_GRANULARITY

  const dateRange = getTimeseriesDateRange(referenceDate, granularity)

  const parentCategoryScores = useOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...dateRange,
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const categoryScores = useChildOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...dateRange,
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const previousCategoryScores = useChildOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...getPreviousTimeseriesDateRange(referenceDate, granularity),
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const bubbleChartData = useMemo(() => {
    return responseAdapter(categoryScores.data || [])
  }, [categoryScores.data])

  const previousBubbleChartData = useMemo(() => {
    return responseAdapter(previousCategoryScores.data || [])
  }, [previousCategoryScores.data])

  const providers = intersection(
    reportProvidersV2,
    bubbleChartData.map( d => d.column )
  )

  const overallScoresByProvider = (parentCategoryScores.data || []).reduce( (acc, d) => {
    const data = get(d.timeseriesByCategory, 'OVERALL', 0)
    const health = data && data.length ? data[data.length - 1].health : null
    acc[d.provider] = health
    return acc
  }, {} as Record<ReportProviderV2, HealthType>)

  const classes = useStyles()

  return (
    <TimeseriesContext.Provider value={{
      ...dateRange,
      minDate,
      maxDate,
      granularity,
    }}>
      <CampaignSetupView
        cardTitle='Advertiser Setup Scores by Platform'
        tooltipContent='Distribution of setup scores by advertiser and ad platform.'
        isError={categoryScores.isError || parentCategoryScores.isError}
        isLoading={categoryScores.isLoading || parentCategoryScores.isLoading}
        isRefetching={categoryScores.isRefetching || parentCategoryScores.isRefetching}
        dataSignal={!!(categoryScores.data && parentCategoryScores.data)}
        columns={
          providers.map( provider => ({
            key: provider,
            label: (
              <Link
                className={classes.providerLink}
                to={`/dashboards/${orgUnit.id}/campaign-setup/${providerURLMap[provider]}`}>
                {/* <ProviderLogo provider={provider} size={1.65} /> */}
                <OpenInNew fontSize='small' className={classes.linkIcon} />
                { providerLabelMap[provider] }
                <Health
                  value={overallScoresByProvider[provider]}
                  color='inherit'
                  animate={false} />
              </Link>
            ),
          }))
        }
        data={bubbleChartData}
        previousData={previousBubbleChartData}
        datumKey='id'
        getPopoverContent={({ key, data }) => (
          <>
          <Box px={2} mt={2}>
            <ProviderLogo provider={key} size={1.35} />
          </Box>
          <List>
            { sortBy(data, 'health').map( org => (
              <ListItem
                key={org.id}
                component={Link}
                className={classes.orgLink}
                button
                to={`/dashboards/${org.id}/campaign-setup`}>
                <OpenInNew fontSize='small' className={classes.icon} />
                { org.name }
                <Health
                  value={org.health}
                  color='inherit'
                  className={classes.health} />
              </ListItem>
            ))}
          </List>
          </>
        )} />
    </TimeseriesContext.Provider>
  )
}
